<template>
  <eg-popover
    ref="imageCoverUploader"
    :class-name="computedClassNames"
    :placement="popoverPlacement"
    :width="popoverWidth"
    @opened="onPopoverOpenClose(true)"
    @hidden="onPopoverOpenClose(false)"
    :disabled="!showPopover"
    :autoHide="false"
    container="body"
  >
    <div slot="popover-button">
      <eg-upload-image
        :placeholder-text="'COVER'"
        :class="[`${shouldShowPlaceholder ? 'border': ''}` , `cover-${lowerCasedType}`, 'material-cover-upload-image']"
        :disable="isImageUploaderDisabled"
        :alt-text="$t('altTextCourseCoverPicture')"
        :img-src="shouldShowPlaceholder ? '' : materialCover"
        :processing="isCoverUploading"
        :tooltip-text="$t('recommendationsForCoverPicture')"
        :text-by-hover="textByHover"
        :label="label"
        :width="width || imageParams.placeholderWidth"
        :wrapper-width="`${width || imageParams.placeholderWidth}px`"
        :height="height || imageParams.placeholderHeight"
        :uploader-max-size="uploaderMaxSize"
        :disable-tooltip="showPopover"
        use-custom-file-loader
        responsive-image
      >
        <template #placeholder>
          <slot name="custom-placeholder-image"></slot>
        </template>
      </eg-upload-image>
    </div>
    <div class="popover-container" slot="popover-bottom-content">
      <div class="title-wrapper">
        <span class="popover-title">{{ $t('userAvatarCoverImage') }}</span>
        <eg-information-tooltip v-if="withTitleTooltip" class="course-cover-info" :maxWidth="305" placement="top">
          <p slot="informationComponent">
            {{ titleTooltipText || $t('courseCoverInfoTip') }}
            <eg-link type="external" :href="'#'">{{ $t('learnMore') }}</eg-link>
          </p>
        </eg-information-tooltip>
      </div>
      <p class="popover-caption">{{ $t('userAvatarCoverCaption') }}</p>
      <div class="popover-wrapper">
        <div class="popover-content">
          <div class="image-block">
            <div
              :class="['popover-image', { empty: shouldShowPlaceholder }]"
            >
              <div v-if="!isResizeModeEnabled" :class="['popover-cover-image', lowerCasedType ]">
                <eg-loader v-show="isCoverUploading" color="green" size="small" />
                <img key="img1"
                     ref="originalImage"
                     class="original-image"
                     :src="originalImage"
                     width="auto"
                     height="auto"
                     alt=""
                     @load="originalImageLoaded"
                />

                <eg-responsive-image
                  :src="`${materialCover}?width=${imageParams.width * 2}&height=${imageParams.height * 2}`"
                  :width="imageParams.width"
                  :height="imageParams.height"
                  :alt-text="`${type} cover`"
                  :class="{ 'responsive-image': isResizeModeEnabled }"
                  :rounding="roundPercent"
                  data-test="cover-popover-uploader-image"
                />
              </div>

              <div v-else :class="{'popover-cover-image': true, [lowerCasedType]: true }">
                <img key="img2"
                     ref="originalImage"
                     class="original-image"
                     :src="originalImage"
                     width="auto"
                     height="auto"
                     alt=""
                     @load="originalImageLoaded"
                />

                <image-cropper
                  class="cropper"
                  v-if="isCropperRendered && isImageLoaded"
                  v-show="isResizeModeEnabled"
                  :imageMetadata="cropperMetadata"
                  :cropperImage="originalImage"
                  :isImageLoaded="!isCoverUploading"
                  :withResizer="true"
                  :withResizeOption="false"
                  :resizeConfig="[]"
                  :staticCanvasWidth="imageParams.width"
                  :staticCanvasHeight="imageParams.height"
                  :isImageInitialized="!isCoverUploading"
                  :isImageUploading="isCoverUploading"
                  :initialImageSize="'custom'"
                  @crop-image-finished="cropImageFinished"
                  @loading-end="setImagePositionHandler"
                />
              </div>
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <dl class="recommendation" v-html="uploadingImageRecommendation"></dl>
          </div>

          <div class="buttons-block">
            <eg-popover
              ref="imageCoverMediaPanelUploader"
              class-name="media-panel-popover"
              :placement="mediaPanelPlacement"
              :whithoutCloseButton="true"
              :whithoutOverlay="true"
              :offset="offset"
              @opened="onMediaPanelOpenClose(true)"
              @hidden="onMediaPanelOpenClose(false)"
            >
              <div slot="popover-button">
                <eg-button
                  v-if="btnText.length < 20"
                  class="popover-upload-button"
                  :fixedWidth="true"
                  :processing="isCoverUploading"
                  tabindex="0"
                  @click="updateFromPC"
                >{{ btnText }}
                </eg-button>
                <eg-button
                  v-else
                  class="popover-upload-button"
                  v-tooltip="{ content: btnText }"
                  :fixedWidth="true"
                  :processing="isCoverUploading"
                  @click="updateFromPC"
                >{{ btnText }}
                </eg-button>
              </div>
            </eg-popover>

            <eg-button
              class="popover-crop-image-btn"
              shape="flat"
              color="light"
              :fixedWidth="true"
              :disabled="shouldDisableCropper"
              @click="activateResizingMode"
            >{{ $t('cropImage') }}
            </eg-button>
          </div>
        </div>
      </div>
    </div>
  </eg-popover>
</template>
   
<script>
import Vue from 'vue';
import { toast, MEDIA_TYPES, localize, utils } from '@easygenerator/fragment-sdk';
import Config from '../config';
import { FILE_SIZES, TWO } from '../constants';
import { USER_ACCOUNT_AVATAR_PICTURE_CHANGED } from '../events';
import ImageCropper from '@/components/ImageCropper/ImageCropper.vue';
import defaultAvatarCover from '@/assets/images/no-avatar.svg';
  
const CROPPER_SHOW_DELAY = 300;
const POPOVER_PLACEMENTS = [
  'auto',
  'auto-start',
  'auto-end',
  'top',
  'top-start',
  'top-end',
  'right',
  'right-start',
  'right-end',
  'bottom',
  'bottom-start',
  'bottom-end',
  'left',
  'left-start',
  'left-end'
];
const DEFAULT_IMAGE_METADATA = {
  defaultScale: 0,
  h: 0,
  scale: null,
  w: 0,
  x: null,
  y: null
};
const DEFAULT_POPOVER_WIDTH = 460;
const DEFAULT_IMAGE_PARAMS = {
  width: 120,
  height: 120,
  placeholderWidth: 60,
  placeholderHeight: 60
};
const USER_AVATAR = 'avatar';
const MAX_FILE_SIZE = 10;
  
const config = Config.getInstance();
const eventBus = new Vue();
  
export default {
  components: {
    ImageCropper
  },
  props: {
    className: {
      type: String,
      default: ''
    },
    cover: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: USER_AVATAR
    },
    label: {
      type: String,
      default: ''
    },
    titleText: {
      type: String,
      default: ''
    },
    captionText: {
      type: String,
      default: ''
    },
    popoverPlacement: {
      type: String,
      default: 'right-start',
      validator: placement => POPOVER_PLACEMENTS.indexOf(placement) !== -1
    },
    width: {
      type: Number,
      default: 0
    },
    height: {
      type: Number,
      default: 0
    },
    uploaderMaxSize: {
      type: Object,
      default: () => ({ width: 175, height: 99 })
    },
    showPopover: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disableCropper: {
      type: Boolean,
      default: false
    },
    withTitleTooltip: {
      type: Boolean,
      default: false
    },
    titleTooltipText: {
      type: String,
      default: ''
    },
    originalImageUrl: {
      type: String,
      default: ''
    },
    metadata: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      uploadingImageRecommendation: this.$t('userAvatarCoverRecommendation'),
      isCoverUploading: false,
      isCropperRendered: false,
      isResizeModeEnabled: false,
      isGifAnimation: false,
      isImageLoaded: false,
      defaultCover: defaultAvatarCover,
      popoverWidth: DEFAULT_POPOVER_WIDTH,
      isImageUploaderDisabled: false,
      mediaType: MEDIA_TYPES.images,
      opened: false,
      embedPlaceholder: '',
      mediaPanelPlacement: 'bottom-start',
      embedData: '',
      offset: '0',
      disableEmbed: false,
      top: '0',
      left: '0',
      btnText: this.$t('changeImage'),
      originalBackground: '',
      imageMetadata: this.metadata || DEFAULT_IMAGE_METADATA,
      initialImageMetadata: this.metadata || DEFAULT_IMAGE_METADATA,
      events: {
        COVER_PICTURE_ADDED: `${this.type} cover picture added`,
        COVER_PICTURE_CHANGED: `${this.type} cover picture changed`
      },
      imagePositionHandler: {}
    };
  },
  computed: {
    isCoverSet() {
      return !!this.cover;
    },
    textByHover() {
      return this.isCoverSet ? this.$t('updateCoverPicture') : this.$t('addCoverPicture');
    },
    materialCover() {
      return this.cover || this.defaultCover;
    },
    originalImage() {
      return this.originalImageUrl || this.originalBackground || this.cover || this.defaultCover;
    },
    imageParams() {
      return DEFAULT_IMAGE_PARAMS;
    },
    cropperMetadata() {
      return this.metadata || this.initialImageMetadata;
    },
    lowerCasedType() {
      return this.type.toLowerCase();
    },
    isCoverEmpty() {
      return !this.materialCover || !this.originalImage.length;
    },
    shouldDisableCropper() {
      this.checkImageExtension(this.originalImage);
  
      return this.disableCropper ||
                this.isGifAnimation ||
                this.shouldShowPlaceholder ||
                this.isCoverEmpty;
    },
    shouldShowPlaceholder() {
      return (!this.materialCover || Object.is(this.materialCover, this.defaultCover)) &&
        (Object.is(this.lowerCasedType, USER_AVATAR));
    },
    roundPercent() {
      return (Object.is(this.lowerCasedType, USER_AVATAR))
        ? '100%'
        : '0';
    },
    computedClassNames() {
      return `image-change-popover ${this.lowerCasedType}-popover ${this.isResizeModeEnabled ? 'resizing' : ''}
        material-editor-popover ${this.className}`;
    }
  },
  methods: {
    async updateFromPC() {
      this.isImageLoaded = false;
      try {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        // Needed for tests, otherwise there's nothing in the DOM to load the avatar into
        document.body.appendChild(input);
        const removeInputTimeout = 3000;

        setTimeout(() => {
          input.remove();
        }, removeInputTimeout);

        input.addEventListener('change', event => {
          const fileInput = event.target;
          const file = fileInput.files && fileInput.files[0];

          if (file) {
            if (file.size > MAX_FILE_SIZE * FILE_SIZES.MB) {
              toast.error(localize('invalidFileSize', { fileTitle: file.name, maxFileSize: '10 MB' }));
              return false;
            }

            const formData = new FormData();
            formData.append('file', file);
            formData.append('isHidden', 'false');
            formData.append(
              'data',
              JSON.stringify({
                prepareResizedImages: [{ width: 30, height: 30, scaleBySmallerSide: true }],
              })
            );

            fetch(`${config.mediaServiceUrl}/api/media/image`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${this.$store.getters['app/token']}`,
              },
              body: formData,
            })
              .then(response => {
                if (!response.ok) {
                  throw new Error('Error: ' + response.status);
                }
                return response.json();
              })
              .then(image => this.applyImageUrl(image.url))
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              .catch(_error => {
                toast.error(localize('invalidFileFormatError', { fileTitle: file.name }));
              });
          }
          return true;
        });
          
        input.click();
      } catch {
        this.showErrorMessage();
      }
    },
    originalImageLoaded() {
      this.isImageLoaded = true;
    },
    async applyImageUrl(imageUrl, isCropped = false) {
      const { HttpWrapper } = utils;
      this.isCoverUploading = true;
  
      if (!isCropped) {
        this.originalBackground = imageUrl;
        this.imageMetadata = { ...DEFAULT_IMAGE_METADATA };
        this.initialImageMetadata = { ...DEFAULT_IMAGE_METADATA };
        this.checkImageExtension(imageUrl);
      }
  
      try {
        await HttpWrapper.put(`${config.authServiceUrl}/api/account/avatar`, {
          authScope: HttpWrapper.scope.api,
          bearerToken: this.$store.getters['app/token'],
          fetchOptions: { credentials: 'include' },
          body: {
            avatarUrl: imageUrl,
            avatarCropperData: {
              originalImage: this.originalBackground,
              initialMetadata: this.imageMetadata
            }
          }
        });

        eventBus.$emit(USER_ACCOUNT_AVATAR_PICTURE_CHANGED, imageUrl);
        this.$emit('cover:uploaded', imageUrl);
      } catch (e) {
        this.$emit('upload:error', e);
      } finally {
        this.isCoverUploading = false;
        if (!isCropped) {
          this.$refs.imageCoverMediaPanelUploader.hide();
          this.setInitialCropperData(imageUrl, { ...DEFAULT_IMAGE_METADATA });
          this.isCropperRendered = true;
          setTimeout(() => {
            if (this.shouldDisableCropper) {
              return;
            }
            this.isResizeModeEnabled = true;
          }, CROPPER_SHOW_DELAY);
        }
      }
    },
    showErrorMessage() {
      toast.error(localize('tryAnotherImageError'));
    },
    onPopoverOpenClose(state) {
      if (this.disabled) {
        return;
      }
      this.isImageUploaderDisabled = state;
      this.isResizeModeEnabled = false;
      this.isCropperRendered = false;
  
      const eventName = `popover:${state ? 'open' : 'close'}`;
      this.$emit(eventName);
    },
    onMediaPanelOpenClose(state) {
      this.opened = state;
    },
    checkImageExtension(url) {
      if (url) {
        const match = url.match(/.+\/([^\\/]+\.([a-zA-Z]+))/);
        this.isGifAnimation = match ? match[TWO] === 'gif' : this.isGifAnimation;
      }
    },
    setImagePositionHandler({ imagePositionHandler }) {
      this.imagePositionHandler = imagePositionHandler;
  
      if (this.initialImageMetadata.scale === null && !this.metadata) {
        imagePositionHandler.containImage();
      }
    },
    async cropImageFinished({ url, imageMetadata }) {
      try {
        this.imageMetadata = { ...imageMetadata };
        // eslint-disable-next-line vue/no-mutating-props
        this.cover = url;
        this.isResizeModeEnabled = false;
        await this.applyImageUrl(url, true);
      } catch (e) {
        this.isResizeModeEnabled = false;
      } finally {
        this.$refs.imageCoverUploader.hide();
      }
    },
    async activateResizingMode() {
      if (this.isCropperRendered || this.shouldDisableCropper) {
        return;
      }

      try {
        const { HttpWrapper } = utils;
        const { cropperData: { originalImage, initialMetadata } } = 
          await HttpWrapper.get(`${config.authServiceUrl}/api/account/profile/avatar`, {
            bearerToken: this.$store.getters['app/token'],
            fetchOptions: { credentials: 'include' },
            authScope: HttpWrapper.scope.api
          });
        this.setInitialCropperData(originalImage, initialMetadata);
      } catch (e) {
        this.$emit('upload:error', e);
      } finally {
        this.isCropperRendered = true;
        setTimeout(() => {
          this.isResizeModeEnabled = true;
        }, CROPPER_SHOW_DELAY);
      }
    },
    setInitialCropperData(url, metadata) {
      this.originalBackground = url || this.cover || this.defaultCover;
      this.initialImageMetadata = metadata && { ...metadata } || { ...DEFAULT_IMAGE_METADATA };
    }
  }
};
</script>

<style lang="less" scoped>
.image-change-popover {
  .popover-container {
    padding: 40px 30px 0 40px;
    color: #42515f;

    .responsive-image {
      position: absolute;
      opacity: 0;
      visibility: hidden;
    }

    .popover-crop-image-btn {
      &:focus-visible {
        outline: 2px solid black;
      }

      /deep/ .btn-icon {
        &.left {
          background: url(~@/assets/images/cropImage.svg) no-repeat center;
          height: 15px;
          width: 15px;
          padding: 0 15px 0 0;
          margin-right: 10px;
        }
      }

      /deep/ .btn-text {
        color: rgba(#363e4e, 0.8);
      }
    }

    .popover-title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      line-height: 1.17;
    }

    .popover-caption {
      margin: 16px 0 24px;
      font-size: 12px;
      line-height: 1.67;
    }

    .popover-wrapper {
      display: flex;
    }

    .popover-content {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr;
      row-gap: 40px;
      width: auto;
      padding-bottom: 40px;

      .image-block {
        display: grid;
        grid-template-columns: 1fr 1fr;
        column-gap: 32px;
      }

      .buttons-block {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: max-content max-content;
        justify-items: left;
        column-gap: 24px;
      }
    }

    .popover-upload-button {
      max-width: 180px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      background: #fc794b !important;

      &:focus-visible {
        outline: 2px solid black;
      }
    }

    .popover-image {
      display: flex;
      flex-direction: column;
      position: relative;
      background: #fff;
      border: 1px solid #e3e5e7;
      border-radius: 3px;

      &.empty {
        border: 1px solid transparent;
      }

      .popover-cover-image {
        position: relative;
        width: 100%;

        .original-image {
          position: absolute;
          visibility: hidden;
          opacity: 0;
        }
      }
    }
  }
}
</style>

<style lang="less">
.border {
  border: solid 2px #fc794b !important;
}

.v-popover {
  &.open {
    .upload-image-container {
      &.cover-avatar {
        z-index: 200;
      }
    }
  }
}

.tooltip.popover.eg-popover.media-panel-popover {
  &[x-placement='top-start'] .wrapper {
    margin-left: 0;
    margin-top: 0;
  }

  &[x-placement^='top']:not(.dropdown-popover-wrapper) {
    margin-bottom: 8px;
  }

  &[x-placement^='bottom']:not(.dropdown-popover-wrapper) {
    margin-top: 8px;
  }

  &[x-placement='bottom-start'] .wrapper {
    margin-top: 0;
  }

  .popover-contents {
    box-shadow: none;
  }

  .popover-arrow {
    display: none;
  }

  .popover-wrapper {
    max-width: 304px;
  }

  .block-menu-action-submenu {
    position: static;
    max-width: 320px;
    margin: 0;
    opacity: 1;
  }
}

.image-change-popover {
  margin-top: 0 !important;

  .responsive-img-wrapper {
    background: transparent;
  }

  .title-wrapper {
    display: flex;

    .v-popover {
      &.course-cover-info {
        display: inline-flex;
        align-items: center;
        margin-left: 8px;
      }
    }

    .title {
      font-size: 11px;
      font-weight: 600;
      color: #42515f;
    }
  }

  &.organization-popover {
    .popover-image {
      height: 120px;
    }
  }

  &.avatar-popover,
  &.rounded-popover {
    .popover-container {
      .popover-content {
        .popover-image {
          width: 120px;
          height: 120px;
          border: 1px solid #e3e5e7;
          border-radius: 50%;
          box-shadow: none;

          &.empty {
            border: 1px solid transparent;
          }
        }

        .image-block {
          grid-template-columns: auto 1fr;
        }
      }
    }

    .responsive-image-wrapper {
      background: transparent;
    }

    .image-block-wrapper {
      .croppa-wrapper {
        .resizable-croppa {
          &.croppa-container {
            max-height: 100%;
            overflow: hidden;
          }
        }

        canvas {
          border-radius: 50%;
        }
      }

      .vdr {
        &.resizable {
          border-radius: 50%;
          box-shadow: 0 0 0 1px #469dd5;

          &::after {
            display: none;
          }
        }
      }
    }
  }

  &.resizing {
    .popover-image {
      .popover-cover-image {
        .original-image {
          position: absolute;
          visibility: hidden;
          opacity: 0;
        }

        &::after {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(255, 255, 255, 0.9);
          z-index: 2;
          user-select: none;
        }

        &.avatar,
        &.rounded {
          .loading-block {
            .eg-pulsar-loader.small {
              width: 30px;
            }
          }

          .responsive-img-wrapper {
            background: transparent;
          }
        }
      }
    }

    .popover-close-btn {
      z-index: 3;
    }
  }
}

.upload-image-container {
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.1);

  .avatar-letter {
    width: 58px !important;
    height: 58px !important;
    background: white;
    display: -ms-flexbox;

    span {
      font-size: 16px;
      font-weight: 700;
      color: #fc794b;
    }
  }

  .avatar-letter.odd,
  .avatar-letter.even {
    background-color: #fff !important;
    border: unset !important;
  }

  &:hover {
    border-color: #fff;
  }

  .responsive-img-wrapper {
    background: transparent;
  }

  &.cover-avatar,
  &.cover-rounded {
    min-width: 60px !important;
    border-radius: 50% !important;

    .loader-container {
      border-radius: 50% !important;

      .eg-pulsar-loader.small {
        width: 30px;
      }
    }

    .image-wrapper {
      border-radius: 50% !important;
    }

    .placeholder {
      border-radius: 50%;
    }

    .overlay {
      border-radius: 50% !important;
      height: 65px !important;
      width: 65px !important;
      top: -3px !important;
      left: -3px !important;

      .icon {
        flex: 0 1 20px !important;
        background-image: url('~@/assets/images/profileHover.svg') !important;
      }
    }
  }

  &.cover-organization {
    z-index: 202;
  }
}

.tooltip.popover.eg-popover {
  &.image-change-popover,
  &.avatar-popover {
    &[x-placement='bottom-start'] {
      margin-top: -4px;
    }

    .popover-close-btn {
      right: 25px;
    }

    &.open {
      .upload-image-container {
        z-index: 200;
      }
    }

    dl {
      font-size: 12px;
      color: #42515f;
      line-height: 1.67;
      word-break: break-word;

      dt {
        font-weight: 600;
      }

      dd {
        margin: 0 0 16px;
      }
    }
  }
}
</style>
